import React, { useEffect, useRef } from "react";
import { Collapse } from "react-bootstrap";
import { CURRENCY } from "../../../contants";
import { useDispatch, useSelector } from "react-redux";
import {
  setDiscount,
  setOtherCharges,
  updateOrderDiscountSocket,
} from "../../../redux/action/orderActions";

// Debounced Other Charges Input (without button) - Same logic as original but with debounce
const OtherChargesInputDebounced = ({
  currentOrder,
  otherCharges,
  dispatch,
}) => {
  const [localCharges, setLocalCharges] = React.useState("");
  const debounceTimerRef = useRef(null);

  React.useEffect(() => {
    setLocalCharges(otherCharges === 0 ? "" : otherCharges ?? "");
  }, [otherCharges]);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setLocalCharges(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Apply charges after debounce (same as original handleApplyCharges)
    debounceTimerRef.current = setTimeout(() => {
      const chargesToApply = value === "" ? 0 : parseFloat(value);
      dispatch(setOtherCharges(chargesToApply));
      dispatch(
        updateOrderDiscountSocket(currentOrder.refId, {
          otherCharges: chargesToApply,
        })
      );
    }, 800);
  };

  return (
    <input
      disabled={currentOrder?.isOrderConfirmed}
      className="form-control form-control-sm no-spinner"
      maxLength="6"
      type="number"
      placeholder="0"
      value={localCharges}
      onChange={handleChange}
    />
  );
};

// Calculate discount percentage
const calculateDiscountPercentage = (discount, value) => {
  let result = (discount * value) / 100;
  return parseFloat(result.toFixed(2));
};

// Debounced Discount Percentage Input (without button) - Same logic as original but with debounce
const DiscountPercentageInputDebounced = ({
  currentOrder,
  getData,
  dispatch,
}) => {
  const [percent, setPercent] = React.useState("");
  const debounceTimerRef = useRef(null);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setPercent(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Apply after debounce (same as original handleApply)
    debounceTimerRef.current = setTimeout(() => {
      const num = value === "" ? 0 : parseFloat(value);
      if (num >= parseFloat(getData().grandTotalWithoutDiscount)) {
        alert("Maximum Discount Reached");
      } else if (num === 0) {
        dispatch(setDiscount(0));
      } else {
        const discount = calculateDiscountPercentage(
          getData().grandTotalWithoutDiscount,
          num
        );
        dispatch(setDiscount(discount));
        dispatch(updateOrderDiscountSocket(currentOrder.refId, { discount }));
      }
    }, 800);
  };

  return (
    <input
      disabled={currentOrder?.isOrderConfirmed}
      maxLength="4"
      min="0"
      max="100"
      className="form-control form-control-sm no-spinner"
      type="number"
      placeholder="0"
      value={percent}
      onChange={handleChange}
    />
  );
};

// Debounced Discount Amount Input (without button) - Same logic as original but with debounce
const DiscountInputDebounced = ({
  currentOrder,
  discount,
  getData,
  dispatch,
}) => {
  const [localDiscount, setLocalDiscount] = React.useState("");
  const debounceTimerRef = useRef(null);

  React.useEffect(() => {
    setLocalDiscount(discount === 0 ? "" : discount ?? "");
  }, [discount]);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setLocalDiscount(value);

    // Immediate Redux update (same as original)
    if (value === "") {
      dispatch(setDiscount(0));
    } else {
      const num = parseFloat(value) || 0;
      if (num >= parseFloat(getData().grandTotalWithoutDiscount)) {
        alert("Maximum Discount Reached");
        dispatch(setDiscount(getData().discount + getData().grandTotal));
      } else {
        dispatch(setDiscount(num));
      }
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounced socket update (same as original handleApplyDiscount)
    debounceTimerRef.current = setTimeout(() => {
      const num = value === "" ? 0 : parseFloat(value);
      dispatch(
        updateOrderDiscountSocket(currentOrder.refId, { discount: num })
      );
    }, 800);
  };

  return (
    <input
      disabled={currentOrder?.isOrderConfirmed}
      maxLength="6"
      min="0"
      className="form-control form-control-sm no-spinner"
      type="number"
      placeholder="0"
      value={localDiscount}
      onChange={handleChange}
    />
  );
};

const CustomerNameInputDebounced = ({
  currentOrder,
  customerName,
  setCustomerName,
  dispatch,
}) => {
  const [localName, setLocalName] = React.useState("");
  const debounceTimerRef = useRef(null);

  React.useEffect(() => {
    setLocalName(customerName || "");
  }, [customerName]);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setLocalName(value);
    setCustomerName(value); // Update parent state immediately for UI

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Save to server after debounce
    debounceTimerRef.current = setTimeout(() => {
      dispatch(
        updateOrderDiscountSocket(currentOrder.refId, { customerName: value })
      );
    }, 800);
  };

  return (
    <input
      type="text"
      className="form-control form-control-sm"
      placeholder="Enter customer name"
      value={localName}
      onChange={handleChange}
      disabled={currentOrder?.isOrderConfirmed}
    />
  );
};

// NEW: Debounced Customer Phone Input
const CustomerPhoneInputDebounced = ({
  currentOrder,
  customerPhone,
  setCustomerPhone,
  dispatch,
  onPhoneChange, // For autocomplete search
  customerData,
  wraperRef,
  display,
  setDisplay,
  numberMatch,
  count,
  SuggestionElement,
  handleClickSuggestion,
}) => {
  const [localPhone, setLocalPhone] = React.useState("");
  const debounceTimerRef = useRef(null);

  React.useEffect(() => {
    setLocalPhone(customerPhone || "");
  }, [customerPhone]);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setLocalPhone(value);
    setCustomerPhone(value); // Update parent state immediately
    onPhoneChange(value); // Trigger autocomplete search

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Save to server after debounce
    debounceTimerRef.current = setTimeout(() => {
      dispatch(
        updateOrderDiscountSocket(currentOrder.refId, { customerMobile: value })
      );
    }, 800);
  };

  return (
    <div className="position-relative">
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Enter mobile number"
        value={localPhone}
        onChange={handleChange}
        autoComplete="off"
        disabled={currentOrder?.isOrderConfirmed}
      />
      {display && numberMatch.length > 0 && (
        <div
          className="position-absolute bg-white w-100 border shadow-sm"
          style={{
            top: "100%",
            left: 0,
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1050,
            marginTop: "4px",
          }}
        >
          {numberMatch.map((item, index) => (
            <div
              key={index}
              className="p-2 cursor-pointer hover-bg-light"
              style={{
                cursor: "pointer",
                fontSize: "12px",
                borderBottom: "1px solid #f0f0f0",
              }}
              onClick={() => handleClickSuggestion(item)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              <div className="font-weight-bold">
                {SuggestionElement(item.customerMobile)}
              </div>
              <div className="text-muted small">{item.customerName}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// NEW: Debounced Remarks Input
const RemarksInputDebounced = ({
  currentOrder,
  remarks,
  setRemarks,
  dispatch,
}) => {
  const [localRemarks, setLocalRemarks] = React.useState("");
  const debounceTimerRef = useRef(null);

  React.useEffect(() => {
    setLocalRemarks(remarks || "");
  }, [remarks]);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setLocalRemarks(value);
    setRemarks(value); // Update parent state immediately

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Save to server after debounce
    debounceTimerRef.current = setTimeout(() => {
      dispatch(
        updateOrderDiscountSocket(currentOrder.refId, { remarks: value })
      );
    }, 800);
  };

  return (
    <textarea
      rows="2"
      className="form-control form-control-sm"
      placeholder="Enter KOT remarks"
      value={localRemarks}
      onChange={handleChange}
      disabled={currentOrder?.isOrderConfirmed}
    />
  );
};

const CustomerDetailsAccordion = ({
  isOpen,
  toggleAccordion,
  customerName,
  setCustomerName,
  customerPhone,
  setCustomerPhone,
  otherCharges,
  setOtherCharges,
  discountPercent,
  setDiscountPercent,
  discountAmount,
  setDiscountAmount,
  currentOrder,
  getData,
  isMobile = false,
  remarks,
  setRemarks,
}) => {
  const dispatch = useDispatch();
  const wraperRef = useRef(null);
  const [count, setCount] = React.useState(0);
  const [display, setDisplay] = React.useState(false);
  const [numberMatch, setNumberMatch] = React.useState([]);

  // Get customer data from Redux
  const { customerData } = useSelector((state) => state.branch);
  const { enableRemark } = useSelector((state) => state.util);

  // Sync with current order data (when switching orders)
  useEffect(() => {
    if (currentOrder) {
      setCustomerName(currentOrder.customerName || "");
      setCustomerPhone(currentOrder.customerMobile || "");
      setRemarks(currentOrder.remarks || "");
    }
  }, [currentOrder?.refId]);

  // Search for matching phone numbers
  const searchNumber = (text) => {
    if (!text) {
      setDisplay(false);
      setNumberMatch([]);
      return;
    }
    const regex = new RegExp(`^${text}.*$`, "i");
    let matches = customerData.filter((data) => {
      return data?.customerMobile?.match(regex);
    });
    if (matches && matches.length > 0) {
      setDisplay(true);
    } else {
      setDisplay(false);
    }
    const mobileNumbers = matches.map((x) => JSON.stringify(x)).map(JSON.parse);
    setNumberMatch(mobileNumbers);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setCustomerPhone(value);
    setCount(value.length);
    searchNumber(value);
  };

  const SuggestionElement = (phone) => {
    let i;
    const number = [];
    for (i = 0; i < count; i++) {
      number.push(
        <strong key={i} style={{ fontWeight: "bold" }}>
          {phone[i]}
        </strong>
      );
    }
    for (i = count; i < 10; i++) {
      number.push(<span key={i}>{phone[i]}</span>);
    }
    return number;
  };

  const handleClickSuggestion = (customer) => {
    setCustomerPhone(customer.customerMobile);
    setCustomerName(customer.customerName);
    setDisplay(false);

    dispatch(
      updateOrderDiscountSocket(currentOrder.refId, {
        customerMobile: customer.customerMobile,
        customerName: customer.customerName,
      })
    );
  };

  const handleClickOutside = (e) => {
    const { current: wrap } = wraperRef;
    if (wrap && !wrap.contains(e.target)) {
      setDisplay(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Don't render if no current order
  if (!currentOrder) {
    return null;
  }

  return (
    <div className="border-top">
      {/* Accordion Header */}
      <div
        className="px-3 py-2 d-flex align-items-center justify-content-between cursor-pointer"
        onClick={toggleAccordion}
        style={{
          cursor: "pointer",
          backgroundColor: isOpen ? "#f8f9fa" : "transparent",
        }}
      >
        <div className="d-flex align-items-center">
          <i className={`mdi mdi-file-document-edit-outline mr-2`}></i>
          <span className="font-weight-medium">Additional Details</span>
        </div>
        <i className={`mdi mdi-chevron-${isOpen ? "up" : "down"}`}></i>
      </div>

      {/* Accordion Content */}
      <Collapse in={isOpen}>
        <div className="px-3 pb-3" style={{maxHeight: "400px", overflowY: "auto"}}>
          {/* Customer Details Section */}
        
            <h6 className="font-weight-bold mb-2" style={{ fontSize: "14px" }}>
              Customer Details
            </h6>

            {/* Phone Number First with Autocomplete */}
            <div className="form-group mb-2">
              <CustomerPhoneInputDebounced
                currentOrder={currentOrder}
                customerPhone={customerPhone}
                setCustomerPhone={setCustomerPhone}
                dispatch={dispatch}
                onPhoneChange={searchNumber}
                customerData={customerData}
                wraperRef={wraperRef}
                display={display}
                setDisplay={setDisplay}
                numberMatch={numberMatch}
                count={count}
                SuggestionElement={SuggestionElement}
                handleClickSuggestion={handleClickSuggestion}
              />
            </div>

            {/* Customer Name */}
            <div className="form-group mb-2">
              <CustomerNameInputDebounced
                currentOrder={currentOrder}
                customerName={customerName}
                setCustomerName={setCustomerName}
                dispatch={dispatch}
              />
            </div>

          {enableRemark && (
            <div className="form-group mb-0">
              <RemarksInputDebounced
                currentOrder={currentOrder}
                remarks={remarks}
                setRemarks={setRemarks}
                dispatch={dispatch}
              />
            </div>
          )}

          {/* Pricing Details Section */}
          <div>
            <h6 className="font-weight-bold mb-2" style={{ fontSize: "14px" }}>
              Pricing Details
            </h6>
            <div className="form-group mb-2">
              <label className="small mb-1">Other Charges ({CURRENCY})</label>
              <OtherChargesInputDebounced
                currentOrder={currentOrder}
                otherCharges={otherCharges}
                dispatch={dispatch}
              />
            </div>
            <div className="row">
              <div className="col-6">
                <div className="form-group mb-0">
                  <label className="small mb-1">Discount (%)</label>
                  <DiscountPercentageInputDebounced
                    currentOrder={currentOrder}
                    getData={getData}
                    dispatch={dispatch}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group mb-0">
                  <label className="small mb-1">
                    Discount Amount ({CURRENCY})
                  </label>
                  <DiscountInputDebounced
                    currentOrder={currentOrder}
                    discount={discountAmount}
                    getData={getData}
                    dispatch={dispatch}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default CustomerDetailsAccordion;
